import { Component, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Router, ActivatedRoute } from '@angular/router';
import { Observable } from 'rxjs';
import { map, startWith } from 'rxjs/operators';
import { ICompany, CompanyType } from 'identity-webapi-client';
import { CustomSituViewWebApiClient } from '../../../services/custom-situ-view-web-api-client.service';
import { ConfigService } from '../../../services/config.service';
import { CompanyService } from '../../../services/company.service';
import {
  LoadProductUsageFromArtifactRequest,
  SearchProductUsageRequest,
  SearchProductUsageResponse,
  ProductUsageEntry,
  ProductUsageSighting,
  GetProductUsageSightingsRequest
} from 'situview-webapi-client';
import {
  ListProductImpressionsRequest,
  SituAnalyticsWebApiClient
} from 'situanalytics-webapi-client';

@Component({
  selector: 'app-product-discover',
  templateUrl: './product-discover.component.html',
  styleUrls: ['./product-discover.component.scss']
})
export class ProductDiscoverComponent implements OnInit {
  private _paginator!: MatPaginator;
  private _sort!: MatSort;

  @ViewChild(MatPaginator) set matPaginator(p: MatPaginator) {
    this._paginator = p;
    if (this.dataSource && p) this.dataSource.paginator = p;
  }
  get paginator(): MatPaginator { return this._paginator; }

  @ViewChild(MatSort) set matSort(s: MatSort) {
    this._sort = s;
    if (this.dataSource && s) this.dataSource.sort = s;
  }
  get sort(): MatSort { return this._sort; }

  searchForm!: FormGroup;
  artifactSearchForm!: FormGroup;
  isLoading = false;
  showManualScan = false;

  displayedColumns: string[] = [
    'preview',
    'product',
    'homeName',
    'companyName',
    'sightingCount',
    'replacementCount',
    'hotspotCount',
    'impressionCount',
    'rooms',
    'actions'
  ];

  readonly impressionWindowHours = 24;
  loadingImpressions = false;

  dataSource!: MatTableDataSource<ProductUsageEntry>;
  totalResults = 0;
  searchPerformed = false;
  allRecords: ProductUsageEntry[] = [];
  situViewUrl: string = '';

  expandedRecord: ProductUsageEntry | null = null;
  expandedRecordSightings: Map<string, ProductUsageSighting[]> = new Map();
  loadingSightings: Set<string> = new Set();

  builders: ICompany[] = [];
  manufacturers: ICompany[] = [];
  filteredCompanies$!: Observable<ICompany[]>;
  filteredManufacturers$!: Observable<ICompany[]>;

  constructor(
    private fb: FormBuilder,
    private apiClient: CustomSituViewWebApiClient,
    private snackBar: MatSnackBar,
    private router: Router,
    private route: ActivatedRoute,
    private configService: ConfigService,
    private companyService: CompanyService,
    private situAnalyticsClient: SituAnalyticsWebApiClient
  ) {
    this.initializeForms();
  }

  ngOnInit(): void {
    this.dataSource = new MatTableDataSource<ProductUsageEntry>([]);
    this.situViewUrl = this.configService.config?.situViewWebApiBaseUrl || '';
    this.readQueryParams();
    this.loadDropdownData();
    this.wireFilteredObservables();

    if (this.artifactSearchForm.value.artifactId) {
      this.showManualScan = true;
      this.loadFromArtifact();
    } else if (this.hasSearchCriteria()) {
      this.searchProductUsage();
    } else {
      this.loadAllProductUsage();
    }
  }

  private initializeForms(): void {
    this.artifactSearchForm = this.fb.group({
      artifactId: ['', [Validators.required]]
    });

    this.searchForm = this.fb.group({
      manufacturer: [''],
      companyId: [''],
      renderedOutputId: [''],
      productId: [''],
      sku: [''],
      limit: [200, [Validators.min(1), Validators.max(1000)]]
    });
  }

  private loadDropdownData(): void {
    this.companyService.companies$.subscribe(items => {
      const all = items || [];
      this.builders = all.filter(c => c.type === CompanyType.Builder)
        .sort((a, b) => (a.name || '').localeCompare(b.name || ''));
      this.manufacturers = all.filter(c => c.type === CompanyType.Manufacturer)
        .sort((a, b) => (a.name || '').localeCompare(b.name || ''));
    });
  }

  private wireFilteredObservables(): void {
    this.filteredManufacturers$ = this.searchForm.controls['manufacturer'].valueChanges.pipe(
      startWith(this.searchForm.value.manufacturer || ''),
      map(v => this.filterManufacturers(v))
    );
    this.filteredCompanies$ = this.searchForm.controls['companyId'].valueChanges.pipe(
      startWith(this.searchForm.value.companyId || ''),
      map(v => this.filterCompanies(v))
    );
  }

  private filterManufacturers(value: string): ICompany[] {
    const v = (value || '').toLowerCase();
    if (!v) return this.manufacturers.slice();
    return this.manufacturers.filter(m => (m.name || '').toLowerCase().includes(v));
  }

  private filterCompanies(value: string): ICompany[] {
    const v = (value || '').toLowerCase();
    if (!v) return this.builders.slice();
    const exactMatch = this.builders.find(c => c.id?.toLowerCase() === v);
    if (exactMatch) return [exactMatch];
    return this.builders.filter(c => (c.name || '').toLowerCase().includes(v));
  }

  displayCompany = (id: string): string => {
    if (!id) return '';
    return this.builders.find(c => c.id === id)?.name || '';
  };

  private readQueryParams(): void {
    const qp = this.route.snapshot.queryParams;
    if (qp['artifactId']) this.artifactSearchForm.patchValue({ artifactId: qp['artifactId'] });
    if (qp['renderedOutputId']) this.searchForm.patchValue({ renderedOutputId: qp['renderedOutputId'] });
    if (qp['productId']) this.searchForm.patchValue({ productId: qp['productId'] });
    if (qp['manufacturer']) this.searchForm.patchValue({ manufacturer: qp['manufacturer'] });
    if (qp['companyId']) this.searchForm.patchValue({ companyId: qp['companyId'] });
    if (qp['sku']) this.searchForm.patchValue({ sku: qp['sku'] });
    if (qp['limit']) {
      const limit = parseInt(qp['limit'], 10);
      if (!isNaN(limit) && limit > 0 && limit <= 1000) this.searchForm.patchValue({ limit });
    }
  }

  private hasSearchCriteria(): boolean {
    const v = this.searchForm.value;
    return !!(v.renderedOutputId || v.productId || v.manufacturer || v.companyId || v.sku);
  }

  private updateUrlParams(): void {
    const queryParams: { [key: string]: any } = {};
    const v = this.searchForm.value;
    if (v.renderedOutputId) queryParams['renderedOutputId'] = v.renderedOutputId;
    if (v.productId) queryParams['productId'] = v.productId;
    if (v.manufacturer) queryParams['manufacturer'] = v.manufacturer;
    if (v.companyId) queryParams['companyId'] = v.companyId;
    if (v.sku) queryParams['sku'] = v.sku;
    if (v.limit && v.limit !== 200) queryParams['limit'] = v.limit;

    this.router.navigate([], {
      relativeTo: this.route,
      queryParams,
      queryParamsHandling: '',
      replaceUrl: true
    });
  }

  toggleManualScan(): void {
    this.showManualScan = !this.showManualScan;
  }

  clearResults(): void {
    this.dataSource.data = [];
    this.allRecords = [];
    this.totalResults = 0;
    this.searchPerformed = false;
    this.expandedRecord = null;
    this.expandedRecordSightings.clear();
  }

  // --- Data loading ---

  async loadFromArtifact(): Promise<void> {
    if (this.artifactSearchForm.invalid) {
      this.snackBar.open('Please enter a valid Artifact ID', 'Dismiss', { duration: 3000 });
      return;
    }
    this.isLoading = true;
    this.searchPerformed = true;

    try {
      const artifactId = this.artifactSearchForm.value.artifactId.trim();
      const ingestResponse = await this.apiClient.loadProductUsageFromArtifact(
        new LoadProductUsageFromArtifactRequest({ renderedOutputId: artifactId })
      );

      if (!ingestResponse?.success) {
        this.snackBar.open(ingestResponse?.errorMessages?.join(', ') || 'Failed to load', 'Dismiss', { duration: 3000 });
        this.clearResults();
        return;
      }

      const searchResponse = await this.apiClient.searchProductUsage(
        new SearchProductUsageRequest({ renderedOutputId: artifactId, limit: 1000 })
      );

      if (searchResponse.success) {
        this.handleSearchResults(searchResponse);
        this.snackBar.open(`Loaded ${this.totalResults} product records`, 'OK', { duration: 3000 });
        this.showManualScan = false;
      } else {
        this.snackBar.open('Scan completed but results could not be loaded', 'Dismiss', { duration: 3000 });
        this.clearResults();
      }
    } catch (error) {
      console.error('Error loading product usage:', error);
      this.snackBar.open('An error occurred', 'Dismiss', { duration: 3000 });
      this.clearResults();
    } finally {
      this.isLoading = false;
    }
  }

  async loadAllProductUsage(): Promise<void> {
    this.isLoading = true;
    this.searchPerformed = true;

    try {
      const response = await this.apiClient.searchProductUsage(
        new SearchProductUsageRequest({ limit: 1000 })
      );
      this.updateUrlParams();

      if (response.success) {
        this.handleSearchResults(response);
      } else {
        this.snackBar.open(response.errorMessages?.join(', ') || 'Failed to load', 'Dismiss', { duration: 3000 });
        this.clearResults();
      }
    } catch (error) {
      console.error('Error loading product usage:', error);
      this.snackBar.open('An error occurred', 'Dismiss', { duration: 3000 });
      this.clearResults();
    } finally {
      this.isLoading = false;
    }
  }

  async searchProductUsage(): Promise<void> {
    this.isLoading = true;
    this.searchPerformed = true;

    try {
      const v = this.searchForm.value;
      const response = await this.apiClient.searchProductUsage(new SearchProductUsageRequest({
        renderedOutputId: v.renderedOutputId?.trim() || undefined,
        productId: v.productId?.trim() || undefined,
        manufacturer: v.manufacturer?.trim() || undefined,
        companyId: v.companyId?.trim() || undefined,
        sku: v.sku?.trim() || undefined,
        limit: v.limit || 200
      }));
      this.updateUrlParams();

      if (response.success) {
        this.handleSearchResults(response);
      } else {
        this.snackBar.open(response.errorMessages?.join(', ') || 'Search failed', 'Dismiss', { duration: 3000 });
        this.clearResults();
      }
    } catch (error) {
      console.error('Error searching product usage:', error);
      this.snackBar.open('An error occurred', 'Dismiss', { duration: 3000 });
      this.clearResults();
    } finally {
      this.isLoading = false;
    }
  }

  private handleSearchResults(response: SearchProductUsageResponse): void {
    const records = response.records || [];
    records.forEach(r => (r as any).impressionCount = 0);
    this.allRecords = records;
    this.dataSource.data = records;
    this.totalResults = records.length;
    this.expandedRecord = null;
    this.expandedRecordSightings.clear();
    if (this.paginator) this.paginator.firstPage();
    this.loadImpressionCounts(records);
  }

  private async loadImpressionCounts(records: ProductUsageEntry[]): Promise<void> {
    if (!records.length) return;
    this.loadingImpressions = true;
    try {
      const dateTo = new Date();
      const dateFrom = new Date(dateTo.getTime() - this.impressionWindowHours * 60 * 60 * 1000);

      const v = this.searchForm.value;
      const manufacturerName = (v.manufacturer || '').trim() || undefined;
      const sku = (v.sku || '').trim() || undefined;

      const request = new ListProductImpressionsRequest({
        dateFrom,
        dateTo,
        manufacturer: manufacturerName,
        productSku: sku,
        pageNumber: 1,
        pageSize: 1000
      });

      const response = await this.situAnalyticsClient.listProductImpressions(request);
      const items = response?.items || [];
      const counts = new Map<string, number>();
      for (const item of items) {
        if (!item.artifactId || !item.productId) continue;
        const key = `${item.artifactId}|${item.productId}`;
        counts.set(key, (counts.get(key) || 0) + (item.totalImpressions || 0));
      }

      for (const record of records) {
        if (!record.renderedOutputId || !record.productId) continue;
        const key = `${record.renderedOutputId}|${record.productId}`;
        (record as any).impressionCount = counts.get(key) || 0;
      }
      // Force table re-render so the new column values are shown.
      this.dataSource.data = [...records];
    } catch (error) {
      console.error('Error loading impression counts:', error);
    } finally {
      this.loadingImpressions = false;
    }
  }

  // --- Sighting drill-in (row expansion) ---

  toggleExpand(record: ProductUsageEntry): void {
    if (this.expandedRecord === record) {
      this.expandedRecord = null;
      return;
    }
    this.expandedRecord = record;
    const id = record.recordId || '';
    if (!id || this.expandedRecordSightings.has(id)) return;
    this.loadSightings(record);
  }

  private async loadSightings(record: ProductUsageEntry): Promise<void> {
    const id = record.recordId || '';
    if (!id) return;
    this.loadingSightings.add(id);
    try {
      const response = await this.apiClient.getProductUsageSightings(
        new GetProductUsageSightingsRequest({ recordId: id })
      );
      if (response.success) {
        this.expandedRecordSightings.set(id, response.sightings || []);
      }
    } catch (error) {
      console.error('Error loading sightings:', error);
    } finally {
      this.loadingSightings.delete(id);
    }
  }

  getSightings(record: ProductUsageEntry): ProductUsageSighting[] {
    return this.expandedRecordSightings.get(record.recordId || '') || [];
  }

  isSightingsLoading(record: ProductUsageEntry): boolean {
    return this.loadingSightings.has(record.recordId || '');
  }

  // --- Navigation ---

  viewHome(record: ProductUsageEntry): void {
    if (!record.renderedOutputId) return;
    const baseUrl = this.configService.config?.situLinkBaseUrl
      ? `https://view.${this.configService.config.situLinkBaseUrl}`
      : (this.configService.config?.situPortalBaseUrl || window.location.origin);
    const url = new URL(baseUrl);
    url.pathname = record.renderedOutputId;
    window.open(url.toString(), '_blank');
  }

  viewProduct(record: ProductUsageEntry): void {
    const path = record.productId
      ? `/products/${record.productId}?tab=7`
      : record.legacyProductId ? `/products/product/${record.legacyProductId}` : null;
    if (!path) return;
    window.open(`${window.location.origin}${path}`, '_blank');
  }

  viewSighting(record: ProductUsageEntry, sighting: ProductUsageSighting): void {
    const baseUrl = this.configService.config?.situLinkBaseUrl
      ? `https://view.${this.configService.config.situLinkBaseUrl}`
      : (this.configService.config?.situPortalBaseUrl || window.location.origin);
    const url = new URL(baseUrl);
    url.pathname = record.renderedOutputId || '/';
    if (sighting.panoramaName) url.searchParams.set('panorama', sighting.panoramaName);
    if (sighting.yaw !== undefined) url.searchParams.set('hlookat', String(Math.round(sighting.yaw)));
    if (sighting.pitch !== undefined) url.searchParams.set('vlookat', String(Math.round(-sighting.pitch)));
    window.open(url.toString(), '_blank');
  }

  searchByManufacturer(manufacturer?: string | null): void {
    if (!manufacturer) return;
    this.searchForm.patchValue({ manufacturer, renderedOutputId: '', productId: '', companyId: '', sku: '', limit: 1000 });
    this.searchProductUsage();
  }

  searchBySku(sku?: string | null): void {
    if (!sku) return;
    this.searchForm.patchValue({ sku, renderedOutputId: '', productId: '', manufacturer: '', companyId: '', limit: 1000 });
    this.searchProductUsage();
  }

  searchByHome(renderedOutputId?: string | null): void {
    if (!renderedOutputId) return;
    this.searchForm.patchValue({ renderedOutputId, productId: '', manufacturer: '', companyId: '', sku: '', limit: 1000 });
    this.searchProductUsage();
  }

  searchByCompany(companyId?: string | null): void {
    if (!companyId) return;
    this.searchForm.patchValue({ companyId, renderedOutputId: '', productId: '', sku: '', limit: 1000 });
    this.searchProductUsage();
  }

  clearSearch(): void {
    this.searchForm.reset({ limit: 200 });
    this.artifactSearchForm.reset();
    this.updateUrlParams();
    this.loadAllProductUsage();
  }

  // --- Export ---

  exportResults(): void {
    if (this.allRecords.length === 0) {
      this.snackBar.open('No data to export', 'Dismiss', { duration: 2000 });
      return;
    }
    const impressionsHeader = `Impressions (${this.impressionWindowHours}h)`;
    const headers = ['Home Name', 'Company Name', 'Manufacturer', 'SKU', 'Replacements', 'Hotspots', 'Sightings', impressionsHeader, 'Rooms', 'Date Scanned', 'Rendered Output ID', 'Product ID'];
    const rows = this.allRecords.map(r => [
      r.homeName || '', r.companyName || '', r.manufacturer || '', r.sku || '',
      r.replacementCount ?? 0, r.hotspotCount ?? 0, r.sightingCount ?? 0,
      (r as any).impressionCount ?? 0,
      (r.rooms || []).map(rm => rm.roomName).join('; '),
      r.dateScanned ? new Date(r.dateScanned).toLocaleString() : '',
      r.renderedOutputId || '', r.productId || ''
    ]);
    const csv = [headers.join(','), ...rows.map(row => row.map(c => `"${c}"`).join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `product-usage-${Date.now()}.csv`;
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  getRoomsList(record: ProductUsageEntry): string {
    return (record.rooms || []).map(r => `${r.roomName} (${r.count})`).join(', ') || '—';
  }

  getPreviewUrl(record: ProductUsageEntry): string | null {
    if (!record.previewArtifactId) return null;
    return `${this.situViewUrl}/home/getimage?artifactid=${record.previewArtifactId}&filename=thumbnail.webp`;
  }

  productDisplayName(record: ProductUsageEntry): string {
    if (record.sku || record.manufacturer) {
      return `${record.sku || 'No SKU'} — ${record.manufacturer || 'Unknown Manufacturer'}`;
    }
    if (record.productId) {
      return `Product ${record.productId.substring(0, 8)}…`;
    }
    return 'Unresolved product';
  }
}
